import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Select,
  DatePicker,
  InputNumber,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import { connect } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import { BOM } from "../../../services/definedMethod";

const INTEREST_TYPE_OPTIONS = [
  { label: "Fixed Interest", value: 0 },
  { label: "Variable Interest", value: 1 },
];

const AML_OPTIONS = [
  { label: "Cleared", value: 100 },
  { label: "Pending", value: 200 },
  { label: "Failed", value: 300 },
];

const YES_NO_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const Tip = ({ text, tip }) => (
  <span>
    {text}
    <Tooltip title={tip}>
      <InfoCircleOutlined style={{ marginLeft: 4, color: "#667085" }} />
    </Tooltip>
  </span>
);

const SectionHeader = ({ title }) => (
  <div style={{ marginBottom: 16, marginTop: 8 }}>
    <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: "#344054" }}>
      {title}
    </p>
    <div style={{ borderBottom: "1px solid #EAECF0", marginTop: 6 }} />
  </div>
);

const AddTreasuryBillContract = (props) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = props;
  const { session } = userData || {};

  const contractId = location.state || null;
  const isEdit = !!contractId;

  const [isLoading, setIsLoading] = useState(false);
  const [clientSearching, setClientSearching] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);

  const [selectedCurrency, setSelectedCurrency] = useState(null);
  // maxFaceValue holds faceValueDenomination from the selected instrument.
  // The face value field pre-populates with this value.
  // User can reduce it but cannot go above it.
  const [maxFaceValue, setMaxFaceValue] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [linkedAccountOptions, setLinkedAccountOptions] = useState([]);
  const [custodianOptions, setCustodianOptions] = useState([]);
  const [amlOptions, setAmlOptions] = useState(AML_OPTIONS);
  const [interestTypeOptions, setInterestTypeOptions] = useState(
    INTEREST_TYPE_OPTIONS,
  );
  const [clientSearchValue, setClientSearchValue] = useState("");
  const [selectedClientId, setSelectedClientId] = useState(null);

  useEffect(() => {
    if (isEdit) fetchContractDetail(contractId);
  }, [contractId]);

  const parseDate = (v) => {
    if (!v) return null;
    if (Array.isArray(v)) {
      const [y, m, d] = v;
      return dayjs(
        `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
      );
    }
    return dayjs(v);
  };

  const handleFinancialChange = () => {
    const faceValue = form.getFieldValue("faceValue");
    const discountRate = form.getFieldValue("discountRate");

    if (faceValue) {
      form.setFieldValue("maturityValue", faceValue);
    }

    if (faceValue && discountRate) {
      const purchasePrice = faceValue - (faceValue * discountRate) / 100;
      form.setFieldValue("purchasePrice", parseFloat(purchasePrice.toFixed(2)));
    }
  };

  const recalculateTenor = () => {
    const settlementDate = form.getFieldValue("settlementDate");
    const maturityDate = form.getFieldValue("maturityDate");
    if (settlementDate && maturityDate) {
      const days = maturityDate.diff(settlementDate, "day");
      form.setFieldValue("tenor", days > 0 ? days : null);
    }
  };

  const searchClients = useCallback(
    (searchValue) => {
      setClientSearchValue(searchValue);
      if (!searchValue || searchValue.length < 2) {
        setClientOptions([]);
        return;
      }
      setClientSearching(true);
      const body = { session };
      const url = `${apiConfig.GetSearch}?exactMatch=true&query=${encodeURIComponent(searchValue.trim())}&resource=clients,clientIdentifiers`;
      BOM.FetchReqAction(body, url, (err, res) => {
        setClientSearching(false);
        if (err) return;
        const items = Array.isArray(res) ? res : [];
        setClientOptions(
          items.map((c) => ({
            label: `${c.entityAccountNo ?? ""} — ${c.entityName ?? ""}`,
            value: c.entityId,
            displayName: c.entityName,
            accountNo: c.entityAccountNo,
            externalId: c.entityExternalId,
          })),
        );
      });
    },
    [session],
  );

  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);
    setMaxFaceValue(null);
    setSelectedCurrency(null);
    form.setFieldsValue({
      productId: null,
      linkedAccountId: null,
      custodian: null,
      faceValue: null,
      purchasePrice: null,
      maturityValue: null,
      tenor: null,
      issueDate: null,
      maturityDate: null,
      settlementDate: null,
    });
    setProductOptions([]);
    setLinkedAccountOptions([]);
    setCustodianOptions([]);
    fetchTemplate(clientId);
  };

  const fetchTemplate = (clientId, skipFinancials = false, onLoaded) => {
    setTemplateLoading(true);
    const body = { session };
    const url = `${apiConfig.GetTreasuryBillContract}/template?clientId=${clientId}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      setTemplateLoading(false);
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load template.",
        );
        return;
      }

      if (Array.isArray(res?.securityMasterOptions)) {
        const mappedProducts = res.securityMasterOptions.map((p) => ({
          label: p.instrumentName,
          value: p.id,
          currencyCode: p.currencyCode ?? null,
          issueDate: p.issueDate ?? null,
          maturityDate: p.maturityDate ?? null,
          tenor: p.tenor ?? null,
          // faceValueDenomination: ceiling for the face value field
          faceValueDenomination: p.faceValueDenomination ?? null,
        }));
        setProductOptions(mappedProducts);
        onLoaded?.(mappedProducts);
      }

      if (Array.isArray(res?.linkedAccountOptions)) {
        setLinkedAccountOptions(
          res.linkedAccountOptions.map((a) => ({
            label: a.accountNo
              ? `${a.accountNo}${a.clientName ? ` — ${a.clientName}` : ""}`
              : `Account ${a.id}`,
            value: a.id,
          })),
        );
      }

      if (Array.isArray(res?.custodians)) {
        setCustodianOptions(
          res.custodians.map((c) => ({
            label: c.name ?? `Custodian ${c.id}`,
            value: c.id,
          })),
        );
      }

      if (Array.isArray(res?.amlScreeningOptions)) {
        setAmlOptions(
          res.amlScreeningOptions.map((a) => ({
            label: a.text,
            value: a.value,
          })),
        );
      }

      if (Array.isArray(res?.interestTypeOptions)) {
        setInterestTypeOptions(
          res.interestTypeOptions.map((i) => ({
            label: i.text,
            value: i.value,
          })),
        );
      }

      const suggested = res?.suggestedCreateRequest ?? {};

      form.setFieldsValues({
        submittedOnDate: parseDate(res.submittedOnDate),
        issueDate: parseDate(res.issueDate),
        settlementDate: parseDate(res.settlementDate),
        tenor: res.tenor ?? suggested.tenor ?? null,
        ...(!skipFinancials && {
          faceValue: res.faceValue ?? null,
          discountRate: res.discountRate ?? null,
          purchasePrice: res.purchasePrice ?? null,
          maturityValue: res.maturityValue ?? null,
        }),
        maturityDate: parseDate(res.maturityDate),
        interestType: res.interestType?.value ?? 0,
        amlScreening: res.amlScreening?.value ?? 100,
        kycValidated: res.kycValidated ?? true,
        withholdTax: res.withholdTax ?? false,
        allowRollover: res.allowRollover ?? false,
        autoRollover: res.autoRollover ?? false,
        autoMaturityTransfer: res.autoMaturityTransfer ?? false,
        centralBankAuctionRefLinked: res.centralBankAuctionRefLinked ?? false,
      });
    });
  };

  // ── Fetch existing contract for edit ──────────────────────────────────────
  const fetchContractDetail = (id) => {
    setIsLoading(true);
    const body = { session };
    const url = `${apiConfig.GetTreasuryBillContract}/${id}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load contract.",
        );
        setIsLoading(false);
        return;
      }

      const clientId = res.client?.id ?? res.clientId;

      if (res.client) {
        setClientOptions([
          {
            label: `${res.client.displayName ?? "Client"} — ${res.client.accountNo ?? clientId}`,
            value: clientId,
          },
        ]);
      }

      setSelectedCurrency(res.currency?.code ?? null);
      setSelectedClientId(clientId);

      if (clientId) {
        fetchTemplate(clientId, true, (products) => {
          const selectedProduct = products.find(
            (p) => p.value === res.product?.id,
          );
          // Restore the cap in edit mode
          if (selectedProduct?.faceValueDenomination) {
            setMaxFaceValue(selectedProduct.faceValueDenomination);
          }
          form.setFieldsValues({
            issueDate: parseDate(selectedProduct?.issueDate),
            maturityDate: parseDate(selectedProduct?.maturityDate),
          });
        });
      }

      form.setFieldsValues({
        clientId,
        productId: res.product?.id ?? null,
        linkedAccountId: res.linkedAccount?.id ?? null,
        custodian: res.custodian?.id ?? null,
        submittedOnDate: dayjs(),
        issueDate: parseDate(res.issueDate),
        settlementDate: parseDate(res.settlementDate),
        tenor: res.tenor ?? null,
        faceValue: res.faceValue ?? null,
        discountRate: res.discountRate ?? null,
        purchasePrice: res.purchasePrice ?? null,
        maturityValue: res.maturityValue ?? null,
        interestType: res.interestType?.value ?? 0,
        amlScreening: res.amlScreening?.value ?? 100,
        kycValidated: res.kycValidated ?? true,
        withholdTax: res.withholdTax ?? false,
        allowRollover: res.allowRollover ?? false,
        autoRollover: res.autoRollover ?? false,
        autoMaturityTransfer: res.autoMaturityTransfer ?? false,
        centralBankAuctionRefLinked: res.centralBankAuctionRefLinked ?? false,
      });

      setIsLoading(false);
    });
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const onFinish = (values) => {
    const body = {
      session,
      locale: "en",
      dateFormat: "yyyy-MM-dd",
      method: isEdit ? "PUT" : "POST",
      clientId: values.clientId,
      productId: values.productId,
      linkedAccountId: values.linkedAccountId,
      custodian: values.custodian,
      submittedOnDate: values.submittedOnDate?.format("YYYY-MM-DD"),
      issueDate: values.issueDate?.format("YYYY-MM-DD"),
      settlementDate: values.settlementDate?.format("YYYY-MM-DD"),
      tenor: values.tenor,
      faceValue: values.faceValue,
      discountRate: values.discountRate,
      purchasePrice: values.purchasePrice,
      maturityValue: values.maturityValue,
      interestType: values.interestType,
      withholdTax: values.withholdTax ?? false,
      amlScreening: values.amlScreening ?? 100,
      kycValidated: values.kycValidated ?? true,
      centralBankAuctionRefLinked: values.centralBankAuctionRefLinked ?? false,
      allowRollover: values.allowRollover ?? false,
      autoRollover: values.autoRollover ?? false,
      autoMaturityTransfer: values.autoMaturityTransfer ?? false,
    };

    const url = isEdit
      ? `${apiConfig.GetTreasuryBillContract}/${contractId}`
      : `${apiConfig.GetTreasuryBillContract}`;

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.errors?.[0]?.defaultUserMessage ||
            err?.defaultUserMessage ||
            (isEdit
              ? "Failed to update contract."
              : "Failed to create contract."),
        );
        setIsLoading(false);
        return;
      }
      BOM.AlertMsg(
        cnt.SUCCESS,
        isEdit
          ? "Contract updated successfully."
          : "Treasury bill contract created successfully.",
      );
      setIsLoading(false);
      navigate(-1);
    });
  };

  const inputStyle = { width: "100%", height: 44 };
  const fmtAmount = (v) =>
    v ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";
  const parseAmount = (v) => Number(v?.replace(/,/g, "") ?? 0);
  const dependentFieldsLocked = !selectedClientId && !isEdit;

  return (
    <MainLayout pageItems={staticConfig.newtreasuryBillContract}>
      <TopContent pageTitle={labelConfig.treasuryBillContract} />

      <Spin spinning={isLoading} size="large">
        <div style={{ padding: "24px" }}>
          <Card
            style={{
              boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
              borderRadius: 20,
            }}
          >
            <h3 style={{ marginBottom: 4, fontWeight: 600 }}>
              {isEdit
                ? "Edit Treasury Bill Contract"
                : "Create Treasury Bill Contract"}
            </h3>
            <p style={{ color: "#667085", fontSize: 14, marginBottom: 24 }}>
              {isEdit
                ? "Update the contract details below."
                : "Select a client first — other fields will populate automatically."}
            </p>

            <Form layout="vertical" form={form} onFinish={onFinish}>
              {/* ── Section 1: Contract Parties ─────────────────────────────── */}
              <SectionHeader title="Contract Parties" />
              <Row>
                <Col span={12} style={{ padding: "0 8px" }}>
                  <Form.Item
                    label={
                      <Tip
                        text="Client"
                        tip="Type account number digits or client name to search."
                      />
                    }
                    name="clientId"
                    rules={[{ required: true, message: "Client is required" }]}
                  >
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" />}
                      options={clientOptions}
                      placeholder="Type account number or name…"
                      showSearch
                      filterOption={false}
                      onSearch={searchClients}
                      onSelect={handleClientSelect}
                      loading={clientSearching}
                      notFoundContent={
                        clientSearching ? (
                          <span style={{ color: "#667085", fontSize: 13 }}>
                            Searching…
                          </span>
                        ) : clientSearchValue.length >= 2 ? (
                          <div
                            style={{ padding: "8px 12px", textAlign: "center" }}
                          >
                            <p
                              style={{
                                margin: 0,
                                fontWeight: 600,
                                color: "#344054",
                                fontSize: 13,
                              }}
                            >
                              No account found
                            </p>
                            <p
                              style={{
                                margin: 0,
                                color: "#667085",
                                fontSize: 12,
                              }}
                            >
                              No client matched "{clientSearchValue}"
                            </p>
                          </div>
                        ) : (
                          <span style={{ color: "#667085", fontSize: 13 }}>
                            Type at least 2 characters
                          </span>
                        )
                      }
                      optionRender={(option) => (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            padding: "2px 0",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: 13,
                              color: "#344054",
                            }}
                          >
                            {option.data.accountNo}
                          </span>
                          <span style={{ fontSize: 12, color: "#667085" }}>
                            {option.data.displayName}
                          </span>
                          {option.data.externalId && (
                            <span style={{ fontSize: 11, color: "#98A2B3" }}>
                              Ref: {option.data.externalId}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Dependent fields ─────────────────────────────────────────── */}
              <Spin spinning={templateLoading} tip="Loading client fields…">
                <Row>
                  {/* Security Instrument */}
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Security Instrument"
                          tip="Available instrument for the selected client."
                        />
                      }
                      name="productId"
                      rules={[
                        { required: true, message: "Product is required" },
                      ]}
                    >
                      <Select
                        style={{ height: 44 }}
                        suffixIcon={<ChevronDown color="#667085" />}
                        options={productOptions}
                        placeholder={
                          dependentFieldsLocked
                            ? "Select a client first"
                            : "Select Product"
                        }
                        onSelect={(value) => {
                          const selected = productOptions.find(
                            (p) => p.value === value,
                          );

                          setSelectedCurrency(selected?.currencyCode ?? null);

                          // ── Store ceiling and pre-fill face value with it ──────
                          const denomination =
                            selected?.faceValueDenomination ?? null;
                          setMaxFaceValue(denomination);

                          const issueDate = parseDate(selected?.issueDate);
                          const maturityDate = parseDate(
                            selected?.maturityDate,
                          );

                          form.setFieldsValue({
                            issueDate,
                            maturityDate,
                            // Pre-fill face value = denomination (user can reduce, never exceed)
                            faceValue: denomination,
                            maturityValue: denomination,
                            purchasePrice: null,
                            tenor: null,
                          });

                          // Trigger financial calc so purchasePrice updates if discountRate already set
                          handleFinancialChange();

                          // Recalculate tenor if settlementDate already has a value
                          setTimeout(() => recalculateTenor(), 0);
                        }}
                        showSearch
                        optionFilterProp="label"
                        disabled={dependentFieldsLocked}
                      />
                    </Form.Item>
                  </Col>

                  {/* Currency — read-only */}
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Currency"
                          tip="Currency of the selected security instrument."
                        />
                      }
                    >
                      <input
                        readOnly
                        value={selectedCurrency ?? "—"}
                        style={{
                          width: "100%",
                          height: 44,
                          background: "#F9FAFB",
                          border: "1px solid #D0D5DD",
                          borderRadius: 6,
                          padding: "0 12px",
                          fontSize: 14,
                          color: "#344054",
                          cursor: "not-allowed",
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  {/* Custodian */}
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Custodian"
                          tip="The custodian holding the treasury bill."
                        />
                      }
                      name="custodian"
                      rules={[
                        { required: true, message: "Custodian is required" },
                      ]}
                    >
                      <Select
                        style={{ height: 44 }}
                        suffixIcon={<ChevronDown color="#667085" />}
                        options={custodianOptions}
                        placeholder={
                          dependentFieldsLocked
                            ? "Select a client first"
                            : "Select Custodian"
                        }
                        showSearch
                        optionFilterProp="label"
                        disabled={dependentFieldsLocked}
                      />
                    </Form.Item>
                  </Col>
                  {/* Linked Account */}
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Linked Account"
                          tip="The client savings account linked to this contract."
                        />
                      }
                      name="linkedAccountId"
                      rules={[
                        {
                          required: true,
                          message: "Linked account is required",
                        },
                      ]}
                    >
                      <Select
                        style={{ height: 44 }}
                        suffixIcon={<ChevronDown color="#667085" />}
                        options={linkedAccountOptions}
                        placeholder={
                          dependentFieldsLocked
                            ? "Select a client first"
                            : "Select Linked Account"
                        }
                        showSearch
                        optionFilterProp="label"
                        disabled={dependentFieldsLocked}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* ── Section 2: Key Dates ──────────────────────────────────── */}
                <SectionHeader title="Key Dates" />
                <Row>
                  {/* Maturity Date — locked from instrument */}
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Maturity Date"
                          tip="The maturity date from the selected security instrument."
                        />
                      }
                      name="maturityDate"
                    >
                      <DatePicker
                        style={inputStyle}
                        format="YYYY-MM-DD"
                        disabled
                        placeholder="Maturity Date"
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Submitted On Date"
                          tip="The date this contract was submitted."
                        />
                      }
                      name="submittedOnDate"
                      rules={[
                        {
                          required: true,
                          message: "Submitted date is required",
                        },
                      ]}
                    >
                      <DatePicker
                        style={inputStyle}
                        format="YYYY-MM-DD"
                        placeholder="YYYY-MM-DD"
                        disabledDate={(current) =>
                          current && current < dayjs().startOf("day")
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  {/* Issue Date — locked from instrument */}
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Issue Date"
                          tip="Set by the selected instrument."
                        />
                      }
                      name="issueDate"
                      rules={[
                        { required: true, message: "Issue date is required" },
                      ]}
                    >
                      <DatePicker
                        style={inputStyle}
                        format="YYYY-MM-DD"
                        placeholder="YYYY-MM-DD"
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  {/* Settlement Date — drives tenor */}

                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Settlement Date"
                          tip="The date funds and securities are exchanged."
                        />
                      }
                      name="settlementDate"
                      rules={[
                        {
                          required: true,
                          message: "Settlement date is required",
                        },
                      ]}
                    >
                      <DatePicker
                        style={inputStyle}
                        format="YYYY-MM-DD"
                        placeholder="YYYY-MM-DD"
                        onChange={() => {
                          // Recalculate tenor = maturityDate − settlementDate
                          setTimeout(() => recalculateTenor(), 0);
                        }}
                        disabledDate={(current) => {
                          const submittedDate =
                            form.getFieldValue("submittedOnDate");
                          if (!current) return false;
                          if (
                            current
                              .startOf("day")
                              .isBefore(dayjs().startOf("day"))
                          )
                            return true;
                          if (
                            submittedDate &&
                            current
                              .startOf("day")
                              .isBefore(submittedDate.startOf("day"))
                          )
                            return true;
                          return false;
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Tenor (Days)"
                          tip="Auto-calculated: days from settlement date to maturity date."
                        />
                      }
                      name="tenor"
                      rules={[{ required: true, message: "Tenor is required" }]}
                    >
                      <InputNumber
                        style={{
                          width: "100%",
                          height: 44,
                          background: "#F9FAFB",
                          cursor: "not-allowed",
                        }}
                        placeholder="Auto-calculated"
                        precision={0}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* ── Section 3: Financial Details ──────────────────────────── */}
                <SectionHeader title="Financial Details" />
                <Row>
                  {/* Face Value — pre-filled from faceValueDenomination, reducible only */}
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text={
                            maxFaceValue
                              ? `Face Value (max: ${maxFaceValue.toLocaleString()})`
                              : "Face Value"
                          }
                          tip={
                            maxFaceValue
                              ? `Pre-filled from the instrument denomination. You may reduce it but cannot exceed ${maxFaceValue.toLocaleString()}.`
                              : "The nominal / par value of the treasury bill."
                          }
                        />
                      }
                      name="faceValue"
                      rules={[
                        { required: true, message: "Face value is required" },
                        {
                          type: "number",
                          min: 1,
                          message: "Must be at least 1",
                        },
                        {
                          validator: (_, value) => {
                            if (maxFaceValue && value && value > maxFaceValue) {
                              return Promise.reject(
                                `Cannot exceed the instrument denomination of ${maxFaceValue.toLocaleString()}`,
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        style={inputStyle}
                        placeholder="e.g. 1,000,000.00"
                        formatter={fmtAmount}
                        parser={parseAmount}
                        precision={2}
                        min={1}
                        max={maxFaceValue ?? undefined}
                        onChange={handleFinancialChange}
                      />
                    </Form.Item>
                  </Col>

                  {/* Discount Rate */}
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Discount Rate (%)"
                          tip="The annualised discount rate applied to this treasury bill."
                        />
                      }
                      name="discountRate"
                      rules={[
                        {
                          required: true,
                          message: "Discount rate is required",
                        },
                        { type: "number", min: 0 },
                      ]}
                    >
                      <InputNumber
                        style={inputStyle}
                        placeholder="e.g. 12.50"
                        min={0}
                        max={100}
                        onChange={handleFinancialChange}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  {/* Purchase Price — auto-computed */}
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Purchase Price"
                          tip="Auto-calculated: face value minus discount."
                        />
                      }
                      name="purchasePrice"
                      rules={[
                        {
                          required: true,
                          message: "Purchase price is required",
                        },
                        { type: "number", min: 1 },
                      ]}
                    >
                      <InputNumber
                        style={{
                          width: "100%",
                          height: 44,
                          background: "#F9FAFB",
                          cursor: "not-allowed",
                        }}
                        placeholder="Auto-calculated"
                        formatter={fmtAmount}
                        parser={parseAmount}
                        precision={2}
                        min={0}
                        disabled
                      />
                    </Form.Item>
                  </Col>

                  {/* Maturity Value — auto-computed */}
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Maturity Value"
                          tip="Auto-calculated: equal to face value."
                        />
                      }
                      name="maturityValue"
                      rules={[
                        {
                          required: true,
                          message: "Maturity value is required",
                        },
                        { type: "number", min: 1 },
                      ]}
                    >
                      <InputNumber
                        style={{
                          width: "100%",
                          height: 44,
                          background: "#F9FAFB",
                          cursor: "not-allowed",
                        }}
                        placeholder="Auto-calculated"
                        formatter={fmtAmount}
                        parser={parseAmount}
                        precision={2}
                        min={0}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="Interest Type"
                          tip="Whether the interest rate is fixed or variable."
                        />
                      }
                      name="interestType"
                      initialValue={0}
                      rules={[
                        {
                          required: true,
                          message: "Interest type is required",
                        },
                      ]}
                    >
                      <Select
                        style={{ height: 44 }}
                        suffixIcon={<ChevronDown color="#667085" />}
                        options={interestTypeOptions}
                        placeholder="Select Interest Type"
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12} style={{ padding: "0 8px" }}>
                    <Form.Item
                      label={
                        <Tip
                          text="AML Screening"
                          tip="Anti-money laundering screening result."
                        />
                      }
                      name="amlScreening"
                      initialValue={100}
                      rules={[
                        {
                          required: true,
                          message: "AML screening is required",
                        },
                      ]}
                    >
                      <Select
                        style={{ height: 44 }}
                        suffixIcon={<ChevronDown color="#667085" />}
                        options={amlOptions}
                        placeholder="Select AML Status"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* ── Section 4: Contract Flags ──────────────────────────────── */}
                <SectionHeader title="Contract Flags" />
                <Row gutter={[24, 16]} style={{ padding: "0 8px" }}>
                  {[
                    {
                      name: "kycValidated",
                      label: "KYC Validated",
                      tip: "Confirm the client's KYC has been validated.",
                      init: true,
                    },
                    {
                      name: "withholdTax",
                      label: "Withhold Tax",
                      tip: "Apply withholding tax on interest earned.",
                      init: false,
                    },
                    {
                      name: "centralBankAuctionRefLinked",
                      label: "Central Bank Auction Ref Linked",
                      tip: "This contract is linked to a central bank auction reference.",
                      init: false,
                    },
                    {
                      name: "allowRollover",
                      label: "Allow Rollover",
                      tip: "Allow this contract to be rolled over at maturity.",
                      init: false,
                    },
                    {
                      name: "autoRollover",
                      label: "Auto Rollover",
                      tip: "Automatically rollover the contract at maturity.",
                      init: false,
                    },
                    {
                      name: "autoMaturityTransfer",
                      label: "Auto Maturity Transfer",
                      tip: "Automatically transfer funds to the linked account.",
                      init: false,
                    },
                  ].map(({ name, label, tip, init }) => (
                    <Col span={8} key={name}>
                      <Form.Item
                        label={<Tip text={label} tip={tip} />}
                        name={name}
                        initialValue={init}
                      >
                        <Select
                          style={{ height: 44 }}
                          suffixIcon={<ChevronDown color="#667085" />}
                          options={YES_NO_OPTIONS}
                          placeholder="Select"
                        />
                      </Form.Item>
                    </Col>
                  ))}
                </Row>

                <div
                  style={{
                    borderTop: "1px solid #EAECF0",
                    margin: "8px 0 20px",
                  }}
                />

                <Row justify="end">
                  <Col>
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      style={{
                        height: 44,
                        padding: "0 24px",
                        borderRadius: 8,
                        border: "1px solid #D0D5DD",
                        background: "#fff",
                        color: "#344054",
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: "pointer",
                        marginRight: 12,
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => form.submit()}
                      style={{
                        height: 44,
                        padding: "0 24px",
                        borderRadius: 8,
                        border: "1px solid #BA24D5",
                        background: "#BA24D5",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: "pointer",
                      }}
                    >
                      {isEdit ? "Update" : "Submit"}
                    </button>
                  </Col>
                </Row>
              </Spin>

              <button type="submit" hidden />
            </Form>
          </Card>
        </div>
      </Spin>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  rightBtn: state.RightSideBtn,
  userData: state.AuthenticationData.userData,
});

export default connect(mapStateToProps, { rightAction })(
  AddTreasuryBillContract,
);
